import { useState } from 'react';
import { ForceTheme } from 'src/common/components/force-theme';
import { useAnonymousSale } from 'src/common/contexts/anonymous-sale-context';
import { ThemeColorMode } from 'src/common/enums';
import { useFeatureValue } from 'src/common/growthbook';
import { Bar, CloseButton, Content, DiscountText, SubscribeBtn, SubscriptionText, TextWrapper } from './anonymous-sale-top-bar.styles';
import { CountdownWithBackground } from './countdown-with-background';
import { BlueFadedOrb, BlueGlowOrb } from './orbs.styles';
const SECOND_VISIT_KEY = 'secondVisitTimestamp';
type AnonymousSaleConfig = {
  waitingPeriodHours: number;
  durationHours: number;
};
interface AnonymousSaleTopBarProps {
  hideCTA?: boolean;
}
export const AnonymousSaleTopBar = ({
  hideCTA
}: AnonymousSaleTopBarProps) => {
  const saleState = useAnonymousSale();
  const [isDismissed, setIsDismissed] = useState(false);
  const saleConfig = useFeatureValue('anonymous-sale-config', {
    waitingPeriodHours: 24,
    durationHours: 24
  }) as AnonymousSaleConfig;
  const saleDurationMs = saleConfig.durationHours * 60 * 60 * 1000;

  /** Expires the sale, effectively deactivating it forever. */
  const handleDismiss = () => {
    try {
      const now = Date.now();
      const sentinel = now - saleDurationMs - 1;
      localStorage.setItem(SECOND_VISIT_KEY, sentinel.toString());
      setIsDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss anonymous sale banner:', error);
    }
  };
  if (!saleState.isActive || isDismissed) {
    return null;
  }
  return <ForceTheme themeMode={ThemeColorMode.DARK} data-sentry-element="ForceTheme" data-sentry-component="AnonymousSaleTopBar" data-sentry-source-file="anonymous-sale-top-bar.tsx">
      <Bar role="region" aria-label="Anonymous Sale Special Offer" data-sentry-element="Bar" data-sentry-source-file="anonymous-sale-top-bar.tsx">
        {/* Large faded background ellipses (Ellipse 15, 16, 17) */}
        <BlueFadedOrb width={68.7} height={50.2} left="calc(50% - 34.35rem)" top="-28.451rem" data-sentry-element="BlueFadedOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueFadedOrb width={107.9} height={97.5} left="calc(50% + 60.95rem)" top="-41.651rem" data-sentry-element="BlueFadedOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueFadedOrb width={107.9} height={97.5} left="calc(50% - 161.35rem)" top="-23.851rem" data-sentry-element="BlueFadedOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueFadedOrb width={107.9} height={97.5} left="calc(50% + 44.95rem)" top="-54.5rem" data-sentry-element="BlueFadedOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />

        {/* Small decorative glowing orbs (Ellipse 84, 82, 85) */}
        <BlueGlowOrb size={3.9} left="0" top="3.6rem" data-sentry-element="BlueGlowOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueGlowOrb size={5.3} right="2.2rem" top="6rem" data-sentry-element="BlueGlowOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueGlowOrb size={9.7} right="20rem" top="-5.8rem" data-sentry-element="BlueGlowOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        <BlueGlowOrb size={9.7} left="9.7rem" top="10.3rem" data-sentry-element="BlueGlowOrb" data-sentry-source-file="anonymous-sale-top-bar.tsx" />

        <Content data-sentry-element="Content" data-sentry-source-file="anonymous-sale-top-bar.tsx">
          <TextWrapper data-sentry-element="TextWrapper" data-sentry-source-file="anonymous-sale-top-bar.tsx">
            <DiscountText data-testid="campaign-promotion-text" data-sentry-element="DiscountText" data-sentry-source-file="anonymous-sale-top-bar.tsx">{saleState.discountPercentage}% discount</DiscountText>
            <SubscriptionText data-sentry-element="SubscriptionText" data-sentry-source-file="anonymous-sale-top-bar.tsx">on annual subscriptions</SubscriptionText>
          </TextWrapper>

          {!hideCTA && <SubscribeBtn to="/why-subscribe" aria-label="Subscribe now">
              Subscribe now
            </SubscribeBtn>}

          <CountdownWithBackground endTime={saleState.until.getTime()} data-sentry-element="CountdownWithBackground" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
        </Content>

        <CloseButton aria-label="Close banner" onClick={handleDismiss} data-sentry-element="CloseButton" data-sentry-source-file="anonymous-sale-top-bar.tsx">
          <svg width="21" height="24" viewBox="0 0 21 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="anonymous-sale-top-bar.tsx">
            <path d="M1.5 1.5L19.5 22.5M19.5 1.5L1.5 22.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-sentry-element="path" data-sentry-source-file="anonymous-sale-top-bar.tsx" />
          </svg>
        </CloseButton>
      </Bar>
    </ForceTheme>;
};